import dlt from "assets/svg/delete.svg";
import ContentWrapper, {
    SimpleModalDataType,
} from "components/content-wrapper/ContentWrapper";
import AddGatewayDropdown from "components/gateways/AddGatewayDropdown";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Button,
    Col,
    Container,
    FormControl,
    InputGroup,
    Modal,
    Row,
} from "react-bootstrap";
import _ from "lodash";
import { Link, useHistory } from "react-router-dom";
import { ArrowIcon, Box } from "components/common";
import { GATEWAY_SELECTION_TYPES, GatewayType } from "constant/gateway";
import {
    deleteRegistry,
    deleteRegistryGroup,
    getAllRegistry,
    getGWReading,
    getRegistryGroups,
} from "service/gatewayService";
import {
    addOrRemoveConfirmLocationPopup,
    checkActive,
    getGatewayType,
    isStandaloneGroup,
} from "utils/gatewayFunctions";

import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { canAccess } from "utils/authorize-action";

import RemindDownloadDataModal from "components/gateways/RemindDownloadDataModal";
import {
    DELETE_GATEWAY_SUCCESS_MESSAGE,
    FETCH_GATEWAY_FAIL_MESSAGE,
    FETCH_GATEWAY_GROUP_FAIL_MESSAGE,
    HttpStatus,
} from "constant";
import { Group } from "types/Gateways";
import { useOrganisation } from "hooks/organization";
import useCollectSort from "hooks/useCollectSort";
import { isHttpSuccess } from "utils/functions";

const gatewayTypeNames = {
    [GatewayType.PCS]: "PCS",
    [GatewayType.PSU]: "PSU",
    [GatewayType.POE]: "POE",
    [GatewayType.LoRaWAN]: "LoRaWAN Node",
};

const AllGateway = () => {
    const history = useHistory();
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [allRegistry, setAllRegistry] = useState<Array<any>>([]);
    const [initialRegistryList, setInitialRegistryList] = useState<Array<any>>(
        []
    );
    const [gatewayToDelete, setGatewayToDelete] = useState<any>(null);
    const [modalShow, setModalShow] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const { data: registryCollect, sortIcon } = useCollectSort(allRegistry);
    const { currentOrgId } = useOrganisation();
    const [simpleModalData, setSimpleModalData] =
        useState<null | SimpleModalDataType>(null);
    const [showRemindDownload, setShowRemindDownload] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedGatewayType, setSelectedGatewayType] = useState<string>(
        GATEWAY_SELECTION_TYPES.LORAWAN
    );
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetch = async () => {
            if (!currentOrgId) {
                return;
            }

            const res = await getAllRegistry(currentOrgId);

            if (res.status === HttpStatus.FORBIDDEN) {
                setIsForbiddenResource(true);
                setIsPageLoading(false);
                return;
            }

            if (!isHttpSuccess(res.status)) {
                // API failed, show empty list
                setAllRegistry([]);
                setInitialRegistryList([]);

                showErrorAlert(getAPIError(res, FETCH_GATEWAY_FAIL_MESSAGE));
                setIsPageLoading(false);
                return;
            }

            let combinedData: Array<any> = [];

            // Get real gateways from API
            if (res.data.length > 0) {
                const hbRes: any = await getGWReading();

                const lastData = hbRes.data.map((i: any) => ({
                    heartbeat: i.heartbeat,
                    gateway_id: i.gateway_id,
                }));

                // Push the heartbeat data to the individual registry
                const cloned = res.data;

                for (const element of cloned) {
                    for (const last of lastData) {
                        if (element.gateway_id === last.gateway_id) {
                            element.heartbeat = last.heartbeat;
                        }
                    }
                }
                combinedData = [...cloned];
            }

            // All gateways including LoRaWAN are now handled by the real API
            setAllRegistry(combinedData);
            setInitialRegistryList(combinedData);
            setIsPageLoading(false);
        };

        if (!canAccess("gateway:read")) {
            setIsPageLoading(false);
            setIsForbiddenResource(true);
        } else {
            fetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrgId]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpen]);

    const searchGateway = (value: any) => {
        if (value !== "") {
            const queryRegistryList: any = [];
            for (const i of initialRegistryList) {
                if (i.name.toLowerCase().includes(value.toLowerCase())) {
                    queryRegistryList.push(i);
                }
            }
            setAllRegistry(queryRegistryList);
        } else {
            setAllRegistry(initialRegistryList);
        }
    };

    const openDeleteConfirmModal = () => {
        setModalShow(true);
        setModalContent(
            `Are you sure you want to delete ${gatewayToDelete.name}?
            This cannot be undone.`
        );
    };

    const closeConfirmModal = () => {
        setModalShow(false);
    };

    const openRemindDownloadModal = (gateway: any) => {
        setGatewayToDelete(gateway);
        setShowRemindDownload(true);
    };

    const handleRemindDownload = async (confirmation: boolean) => {
        setShowRemindDownload(false);

        if (confirmation) {
            openDeleteConfirmModal();
        }
    };

    const handleDeleteGateway = async () => {
        closeConfirmModal();

        // Delete gateway via API (works for all gateway types including LoRaWAN)
        const response = await deleteRegistry(gatewayToDelete.gateway_id);

        if (isHttpSuccess(response.status)) {
            const cloned = [...allRegistry].filter((i: any) => {
                return i.gateway_id !== gatewayToDelete.gateway_id;
            });
            const initialCloned = [...initialRegistryList].filter((i: any) => {
                return i.gateway_id !== gatewayToDelete.gateway_id;
            });
            setAllRegistry(cloned);
            setInitialRegistryList(initialCloned);
            addOrRemoveConfirmLocationPopup(gatewayToDelete.gateway_id, false);

            showSuccessAlert({
                message: DELETE_GATEWAY_SUCCESS_MESSAGE,
            });
        } else {
            setSimpleModalData({
                resObj: response,
            } as SimpleModalDataType);
        }
    };

    const renderGroupDetails = (groupDetails: any) => {
        if (groupDetails) {
            return <span>{groupDetails.name}</span>;
        }
        return <span className="standalone">Standalone</span>;
    };

    const renderRegistryItems = () => {
        return registryCollect.map((p: any) => {
            const gatewayType = getGatewayType(p.info);
            const gatewayTypeName = _.get(
                gatewayTypeNames,
                gatewayType || "",
                "Unknown"
            );

            return (
                <React.Fragment key={p.gateway_id}>
                    <div className="table-row" key={p.gateway_id}>
                        <Row className="no-checkbox">
                            <div className="gatewayList-row">
                                <div className="gatewayList-row-middle">
                                    <div className="gatewayList-row-name">
                                        <Link
                                            to={{
                                                pathname: `/gateway-details/${p.gateway_id}`,
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: p.name.replace(
                                                    / /g,
                                                    "&nbsp;"
                                                ),
                                            }}
                                        ></Link>
                                    </div>
                                    <div className="gatewayList-row-uuid">
                                        <span className="reading-label">
                                            UUID:{" "}
                                        </span>
                                        {p.gateway_id}
                                    </div>
                                    <div className="gatewayList-row-type">
                                        <span className="reading-label">
                                            Type:{" "}
                                        </span>
                                        {gatewayTypeName}
                                    </div>
                                    <div className="gatewayList-row-last-active">
                                        {checkActive(p.heartbeat, !!p.info)}
                                    </div>
                                    <div className="gatewayList-row-group">
                                        <span className="reading-label">
                                            Group:{" "}
                                        </span>
                                        {renderGroupDetails(p.group)}
                                    </div>
                                </div>
                                <div className="gatewayList-row-end">
                                    <div className="gatewayList-row-delete-action">
                                        <HoverAuthorizeTooltip permission="gateway:delete">
                                            <img
                                                src={dlt}
                                                alt="delete"
                                                className="btn"
                                                onClick={() => {
                                                    openRemindDownloadModal(p);
                                                }}
                                                onKeyDown={() => {
                                                    openRemindDownloadModal(p);
                                                }}
                                            />
                                        </HoverAuthorizeTooltip>
                                    </div>
                                    <div className="gatewayList-row-detail-action">
                                        <Link
                                            to={{
                                                pathname: `/gateway-details/${p.gateway_id}`,
                                            }}
                                        >
                                            <i className="material-icons right">
                                                keyboard_arrow_right
                                            </i>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </Row>
                    </div>
                </React.Fragment>
            );
        });
    };

    const handleGatewayTypeSelect = (type: string) => {
        setSelectedGatewayType(type);
        setDropdownOpen(false);
    };

    const handleAddGateway = useCallback(() => {
        // Navigate based on selected gateway type
        const route =
            selectedGatewayType === GATEWAY_SELECTION_TYPES.IOTPORTAL
                ? "/gateways-add"
                : "/lorawan-add";
        history.push(route);
    }, [selectedGatewayType, history]);

    const toggleDropdown = useCallback(() => {
        setDropdownOpen((prev) => !prev);
    }, []);

    const renderEmpty = useMemo(
        () => (
            <Row>
                <Col sm="12" className="mt-2">
                    <div className="empty-widget-container text-center">
                        <h3 className="mb-3">No Gateways Added</h3>
                        <h5 className="mb-3">Choose a gateway type to add</h5>
                        <div className="gateway-empty-container">
                            <div
                                className="position-relative w-100"
                                ref={dropdownRef}
                            >
                                <HoverAuthorizeTooltip permission="gateway:create">
                                    <Box
                                        className="d-flex gateway-selector-box"
                                        onClick={toggleDropdown}
                                        data-testid="gateway-type-selector"
                                    >
                                        <div>{selectedGatewayType}</div>
                                        <div className="ml-auto">
                                            <ArrowIcon className="material-icons align-middle">
                                                {dropdownOpen
                                                    ? "keyboard_arrow_down"
                                                    : "keyboard_arrow_right"}
                                            </ArrowIcon>
                                        </div>
                                    </Box>
                                </HoverAuthorizeTooltip>

                                {dropdownOpen && (
                                    <div className="gateway-dropdown-menu">
                                        <button
                                            type="button"
                                            className="gateway-dropdown-item"
                                            onClick={() =>
                                                handleGatewayTypeSelect(
                                                    GATEWAY_SELECTION_TYPES.IOTPORTAL
                                                )
                                            }
                                            data-testid="iotportal-gateway-option"
                                        >
                                            {GATEWAY_SELECTION_TYPES.IOTPORTAL}
                                        </button>
                                        <button
                                            type="button"
                                            className="gateway-dropdown-item"
                                            onClick={() =>
                                                handleGatewayTypeSelect(
                                                    GATEWAY_SELECTION_TYPES.LORAWAN
                                                )
                                            }
                                            data-testid="lorawan-node-option"
                                        >
                                            {GATEWAY_SELECTION_TYPES.LORAWAN}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <HoverAuthorizeTooltip
                                permission="gateway:create"
                                className="w-100"
                            >
                                <Button
                                    variant="primary"
                                    className="add-gateway-btn"
                                    onClick={handleAddGateway}
                                    data-testid="add-gateway-button"
                                >
                                    Add a Gateway
                                </Button>
                            </HoverAuthorizeTooltip>
                        </div>
                    </div>
                </Col>
            </Row>
        ),
        [toggleDropdown, selectedGatewayType, dropdownOpen, handleAddGateway]
    );

    return (
        <ContentWrapper
            isForbiddenResource={isForbiddenResource}
            title="All Gateways"
            isLoading={isPageLoading}
            simpleModalData={simpleModalData}
        >
            <div className="page-content gateway-list-page">
                <Container fluid>
                    <Row>
                        <Col sm={12}>
                            <h5 className="page-title">All Gateways</h5>
                        </Col>
                    </Row>

                    {initialRegistryList.length === 0 &&
                        !isPageLoading &&
                        renderEmpty}

                    {initialRegistryList.length > 0 && (
                        <>
                            <Row>
                                <Col
                                    sm={12}
                                    className="d-md-flex justify-content-end order-md-2 order-1"
                                >
                                    <HoverAuthorizeTooltip permission="gateway:create">
                                        <AddGatewayDropdown
                                            aria-label="Add Gateway - Choose gateway type to add"
                                            data-testid="all-gateway-dropdown"
                                        />
                                    </HoverAuthorizeTooltip>

                                    <div className="search">
                                        <InputGroup>
                                            <FormControl
                                                type="text"
                                                placeholder="Search for.."
                                                aria-label="search-gateway"
                                                aria-describedby="button-addon2"
                                                onChange={(e) =>
                                                    searchGateway(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </InputGroup>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="cstm-table mt-4">
                                <Col sm={12}>
                                    <div className="table-head">
                                        <Row className="no-checkbox">
                                            <Col>
                                                Gateway Name {sortIcon("name")}
                                            </Col>
                                            <Col>
                                                UUID {sortIcon("gateway_id")}
                                            </Col>
                                            <Col>Type</Col>
                                            <Col>
                                                Last Active{" "}
                                                {sortIcon("heartbeat")}
                                            </Col>
                                            <Col>
                                                Group {sortIcon("group.name")}
                                            </Col>
                                            <Col sm="1">Action</Col>
                                            <Col sm="1"></Col>
                                        </Row>
                                    </div>
                                    <div>{renderRegistryItems()}</div>
                                    <div
                                        className={
                                            allRegistry.length === 0
                                                ? "mt-5 text-center d-block"
                                                : "mt-5 text-center d-none"
                                        }
                                    >
                                        No gateway found.
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                    <Modal
                        centered
                        show={modalShow}
                        onHide={closeConfirmModal}
                        backdrop="static"
                        keyboard={false}
                        aria-labelledby="example-modal-sizes-title-sm"
                        className="no-header danger"
                    >
                        <Modal.Body className="text-center mt-3 mb-3">
                            <div className="modal-icon-box">
                                <span className="material-icons">delete</span>
                            </div>
                            <h3 className="mb-3">Delete Gateway?</h3>
                            <p className="mb-4">{modalContent}</p>
                            <Button
                                variant="secondary"
                                onClick={closeConfirmModal}
                            >
                                CANCEL
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDeleteGateway}
                            >
                                DELETE
                            </Button>
                        </Modal.Body>
                    </Modal>
                </Container>
            </div>
            <RemindDownloadDataModal
                show={showRemindDownload}
                onConfirm={handleRemindDownload}
            />
        </ContentWrapper>
    );
};

const GatewayGroups = () => {
    const [gatewayGroups, setGatewayGroups] = useState<Array<any>>([]);
    const [initialGatewayGroups, setInitialGatewayGroups] = useState<
        Array<any>
    >([]);
    const [modalShow, setModalShow] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const [gatewayGroupToDelete, setGatewayGroupToDelete] = useState({
        name: "",
        uuid: "",
    });
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const { data: gatewayGroupsCollection, sortIcon } =
        useCollectSort(gatewayGroups);
    const [simpleModalData, setSimpleModalData] =
        useState<null | SimpleModalDataType>(null);

    useEffect(() => {
        const fetch = async () => {
            const response = await getRegistryGroups();
            if (isHttpSuccess(response.status)) {
                // API will return an empty array of data if there is no gateway
                // Filtering the standalone gateways
                const lst = response.data?.filter(
                    (group: Group) => !isStandaloneGroup(group)
                );
                setGatewayGroups(lst);
                setInitialGatewayGroups(lst);
            } else if (response.status === HttpStatus.FORBIDDEN) {
                setIsForbiddenResource(true);
            } else {
                showErrorAlert(
                    getAPIError(response, FETCH_GATEWAY_GROUP_FAIL_MESSAGE)
                );
            }
            setIsPageLoading(false);
        };

        if (!canAccess("gateway:read")) {
            setIsPageLoading(false);
            setIsForbiddenResource(true);
        } else {
            fetch();
        }
    }, []);

    const openConfirmationDeleteModal = (name: string) => {
        setModalShow(true);
        setModalContent(`Are you sure you want to delete ${name}?`);
    };

    const closeConfirmModal = () => {
        setModalShow(false);
    };

    const handleDelete = async () => {
        closeConfirmModal();
        const response = await deleteRegistryGroup(gatewayGroupToDelete.uuid);

        if (isHttpSuccess(response.status)) {
            const cloned: any = [...gatewayGroups].filter((i: any) => {
                return i.uuid !== gatewayGroupToDelete.uuid;
            });
            const initialCloned: any = [...initialGatewayGroups].filter(
                (i: any) => {
                    return i.uuid !== gatewayGroupToDelete.uuid;
                }
            );
            setGatewayGroups(cloned);
            setInitialGatewayGroups(initialCloned);
            showSuccessAlert({
                message: `${gatewayGroupToDelete.name} deleted successfully.`,
            });
        } else {
            setSimpleModalData({
                resObj: response,
            } as SimpleModalDataType);
        }
    };

    const renderGatewayGroups = () => {
        const lst = gatewayGroupsCollection.map((gateway) => {
            return (
                <div className="table-row" key={gateway.uuid}>
                    <Row className="no-checkbox">
                        <div className="gatewayList-row">
                            <div className="gatewayList-row-middle">
                                <div className="gatewayList-row-group-name">
                                    <Link
                                        to={{
                                            pathname: `/gate-group-details/${gateway.uuid}`,
                                            state: {
                                                groupName: gateway.name,
                                            },
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: gateway.name.replace(
                                                / /g,
                                                "&nbsp;"
                                            ),
                                        }}
                                    ></Link>
                                </div>
                                <div className="gatewayList-row-group-members">
                                    <span className="reading-label">
                                        Members:{" "}
                                    </span>
                                    {gateway.gateways?.length}
                                </div>
                            </div>
                            <div className="gatewayList-row-end">
                                <div className="gatewayList-row-delete-action">
                                    <HoverAuthorizeTooltip permission="gateway:delete">
                                        <img
                                            src={dlt}
                                            alt="delete"
                                            className="btn"
                                            onClick={() => {
                                                setGatewayGroupToDelete({
                                                    name: gateway.name,
                                                    uuid: gateway.uuid,
                                                });
                                                openConfirmationDeleteModal(
                                                    gateway.name
                                                );
                                            }}
                                            onKeyDown={() => {
                                                setGatewayGroupToDelete({
                                                    name: gateway.name,
                                                    uuid: gateway.uuid,
                                                });
                                                openConfirmationDeleteModal(
                                                    gateway.name
                                                );
                                            }}
                                        />
                                    </HoverAuthorizeTooltip>
                                </div>
                                <div className="gatewayList-row-detail-action">
                                    <Link
                                        to={{
                                            pathname: `/gate-group-details/${gateway.uuid}`,
                                            state: {
                                                groupName: gateway.name,
                                            },
                                        }}
                                    >
                                        {" "}
                                        <i className="material-icons right">
                                            keyboard_arrow_right
                                        </i>{" "}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </Row>
                </div>
            );
        });
        return lst;
    };

    const searchGroupGateway = (query: string) => {
        if (query !== "") {
            const filtered: any = [];
            initialGatewayGroups.forEach((i) => {
                if (i.name.toLowerCase().includes(query.toLowerCase())) {
                    filtered.push(i);
                }
            });
            setGatewayGroups(filtered);
        } else {
            setGatewayGroups(initialGatewayGroups);
        }
    };

    const renderNoContent = () => {
        return (
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm="12">
                            <h5 className="page-title">Gateway Groups</h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12" className="mt-2">
                            <div className="empty-widget text-center">
                                <h3 className="mb-3">
                                    No Gateway Groups Added
                                </h3>
                                <h5 className="mb-3">
                                    Add a New Gateway Group
                                </h5>
                                <HoverAuthorizeTooltip permission="gateway:create">
                                    <Link
                                        to="/new-group"
                                        className="btn btn-primary mt-3"
                                    >
                                        + New Group
                                    </Link>
                                </HoverAuthorizeTooltip>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    };

    return (
        <ContentWrapper
            simpleModalData={simpleModalData}
            isForbiddenResource={isForbiddenResource}
            title="Gateway Groups"
            isLoading={isPageLoading}
        >
            {initialGatewayGroups.length === 0 ? (
                renderNoContent()
            ) : (
                <div className="page-content gateway-list-page">
                    <Container fluid>
                        <Row>
                            <Col sm={12}>
                                <h5 className="page-title">Gateway Groups</h5>
                            </Col>
                        </Row>

                        <Row>
                            <Col
                                sm={12}
                                className="d-md-flex justify-content-end order-md-2 order-1"
                            >
                                <HoverAuthorizeTooltip permission="gateway:create">
                                    <Link
                                        to="/new-group"
                                        className="btn btn-primary add-gateway mr-3"
                                    >
                                        + New Group
                                    </Link>
                                </HoverAuthorizeTooltip>
                                <div className="search">
                                    <InputGroup>
                                        <FormControl
                                            type="text"
                                            placeholder="Search for.."
                                            aria-describedby="button-addon2"
                                            aria-label="search-group"
                                            onChange={(e) => {
                                                searchGroupGateway(
                                                    e.target.value
                                                );
                                            }}
                                        />
                                    </InputGroup>
                                </div>
                            </Col>
                        </Row>
                        <Row className="cstm-table mt-4">
                            <Col sm={12}>
                                <div className="table-head">
                                    <Row className="no-checkbox">
                                        <Col>Group Name {sortIcon("name")}</Col>
                                        <Col>
                                            Members{" "}
                                            {sortIcon("gateways.length")}
                                        </Col>

                                        <Col sm="1">Action</Col>
                                        <Col sm="1"></Col>
                                    </Row>
                                </div>
                                {renderGatewayGroups()}
                                <div
                                    className={
                                        gatewayGroups.length === 0
                                            ? "mt-5 text-center d-block"
                                            : "mt-5 text-center d-none"
                                    }
                                >
                                    No gateway group found.
                                </div>
                            </Col>
                        </Row>
                        <Modal
                            centered
                            show={modalShow}
                            onHide={closeConfirmModal}
                            backdrop="static"
                            keyboard={false}
                            aria-labelledby="example-modal-sizes-title-sm"
                            className="no-header danger"
                        >
                            <Modal.Body className="text-center mt-3 mb-3">
                                <div className="modal-icon-box">
                                    <span className="material-icons">
                                        delete
                                    </span>
                                </div>
                                <h3 className="mb-3">Confirmation</h3>
                                <p className="mb-4">{modalContent}</p>
                                <>
                                    <Button
                                        variant="secondary"
                                        onClick={closeConfirmModal}
                                    >
                                        CANCEL
                                    </Button>

                                    <Button
                                        variant="danger"
                                        onClick={() => {
                                            handleDelete();
                                        }}
                                    >
                                        DELETE
                                    </Button>
                                </>
                            </Modal.Body>
                        </Modal>
                    </Container>
                </div>
            )}
        </ContentWrapper>
    );
};

const Standalone = () => {
    const history = useHistory();
    const [standaloneGatewayList, setStandaloneGatewayList] = useState<
        Array<any>
    >([]);
    const [initialStandaloneGatewayList, setInitialStandaloneGatewayList] =
        useState<Array<any>>([]);
    const [simpleModalData, setSimpleModalData] =
        useState<null | SimpleModalDataType>(null);
    const [modalShow, setModalShow] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [gatewayToDelete, setGatewayToDelete] = useState<any>(null);
    const { data: standaloneGatewayListCollect, sortIcon } = useCollectSort(
        standaloneGatewayList
    );
    const [showRemindDownload, setShowRemindDownload] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            const res: any = await getAllRegistry();
            setIsPageLoading(false);

            if (res.status === HttpStatus.FORBIDDEN) {
                setIsForbiddenResource(true);
                return;
            }

            if (!isHttpSuccess(res.status)) {
                showErrorAlert(getAPIError(res, FETCH_GATEWAY_FAIL_MESSAGE));
                return;
            }

            if (res.data.length <= 0) {
                return;
            }

            // Filter to get registry that is standalone or no group
            const standaloneLst = res.data.filter((i: any) => !i.group);

            const hbRes: any = await getGWReading();
            const lastData: any = hbRes.data.map((i: any) => {
                const items = {
                    heartbeat: i.heartbeat,
                    gateway_id: i.gateway_id,
                };
                return items;
            });

            // Push the heartbeat data to the individual registry
            const cloned = standaloneLst;
            for (const element of cloned) {
                for (const last of lastData) {
                    if (element.gateway_id === last.gateway_id) {
                        element.heartbeat = last.heartbeat;
                    }
                }
            }
            setStandaloneGatewayList(cloned);
            setInitialStandaloneGatewayList(cloned);
        };

        if (!canAccess("gateway:read")) {
            setIsPageLoading(false);
            setIsForbiddenResource(true);
        } else {
            fetch();
        }
    }, [history]);

    const searchGateway = (query: string) => {
        if (query !== "") {
            const filtered = initialStandaloneGatewayList.filter((gateway) =>
                gateway.name.toLowerCase().includes(query.toLowerCase())
            );
            setStandaloneGatewayList(filtered);
        } else {
            setStandaloneGatewayList(initialStandaloneGatewayList);
        }
    };

    const openDeleteConfirmModal = () => {
        setModalShow(true);
        setModalContent(
            `Are you sure you want to delete ${gatewayToDelete.name}?
            This cannot be undone.`
        );
    };

    const closeConfirmModal = () => {
        setModalShow(false);
    };

    const openRemindDownloadModal = (gateway: any) => {
        setGatewayToDelete(gateway);
        setShowRemindDownload(true);
    };

    const handleRemindDownload = async (confirmation: boolean) => {
        setShowRemindDownload(false);

        if (confirmation) {
            openDeleteConfirmModal();
        }
    };

    const handleDeleteGateway = async () => {
        closeConfirmModal();
        try {
            const response = await deleteRegistry(gatewayToDelete.gateway_id);
            if (isHttpSuccess(response.status)) {
                const cloned = [...standaloneGatewayList].filter((i: any) => {
                    return i.gateway_id !== gatewayToDelete.gateway_id;
                });
                const initialCloned = [...initialStandaloneGatewayList].filter(
                    (i: any) => {
                        return i.gateway_id !== gatewayToDelete.gateway_id;
                    }
                );
                setStandaloneGatewayList(cloned);
                setInitialStandaloneGatewayList(initialCloned);
                addOrRemoveConfirmLocationPopup(
                    gatewayToDelete.gateway_id,
                    false
                );
                showSuccessAlert({
                    message: DELETE_GATEWAY_SUCCESS_MESSAGE,
                });
            } else {
                setSimpleModalData({
                    resObj: response,
                } as SimpleModalDataType);
            }
        } catch (err: any) {
            setSimpleModalData({
                resObj: err,
            } as SimpleModalDataType);
        }
    };

    const renderStandalone = () => {
        const lst = standaloneGatewayListCollect.map((gateway) => {
            return (
                <div className="table-row" key={gateway.gateway_id}>
                    <Row className="no-checkbox">
                        <div className="gatewayList-row">
                            <div className="gatewayList-row-middle">
                                <div className="gatewayList-row-standalone-gateway-name">
                                    <Link
                                        to={{
                                            pathname: `/gateway-details/${gateway.gateway_id}`,
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: gateway.name.replace(
                                                / /g,
                                                "&nbsp;"
                                            ),
                                        }}
                                    ></Link>
                                </div>
                                <div className="gatewayList-row-standalone-gateway-uuid">
                                    {" "}
                                    <span className="reading-label">UUID:</span>
                                    {gateway.gateway_id}
                                </div>
                                <div className="gatewayList-row-standalone-gateway-last-active">
                                    {" "}
                                    {checkActive(
                                        gateway.heartbeat,
                                        !!gateway.info
                                    )}
                                </div>
                            </div>
                            <div className="gatewayList-row-end">
                                <div className="gatewayList-row-delete-action">
                                    {" "}
                                    <HoverAuthorizeTooltip permission="gateway:delete">
                                        <img
                                            src={dlt}
                                            alt="delete"
                                            className="btn"
                                            onClick={() => {
                                                openRemindDownloadModal(
                                                    gateway
                                                );
                                            }}
                                            onKeyDown={() => {
                                                openRemindDownloadModal(
                                                    gateway
                                                );
                                            }}
                                        />
                                    </HoverAuthorizeTooltip>
                                </div>
                                <div className="gatewayList-row-detail-action">
                                    <Link
                                        to={{
                                            pathname: `/gateway-details/${gateway.gateway_id}`,
                                        }}
                                    >
                                        <i className="material-icons right">
                                            keyboard_arrow_right
                                        </i>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </Row>
                </div>
            );
        });
        return lst;
    };

    return (
        <ContentWrapper
            isLoading={isPageLoading}
            isForbiddenResource={isForbiddenResource}
            title="Standalone Gateways"
            simpleModalData={simpleModalData}
        >
            <div className="page-content gateway-list-page">
                <Container fluid>
                    <Row>
                        <Col sm={12}>
                            <h5 className="page-title">Standalone Gateways</h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col
                            sm={12}
                            className="d-md-flex justify-content-end order-md-2 order-1"
                        >
                            <HoverAuthorizeTooltip permission="gateway:create">
                                <AddGatewayDropdown
                                    aria-label="Add Gateway - Choose gateway type to add"
                                    data-testid="standalone-gateway-dropdown"
                                />
                            </HoverAuthorizeTooltip>
                            <div className="search">
                                <InputGroup>
                                    <FormControl
                                        type="text"
                                        placeholder="Search for.."
                                        aria-label="search-gateway"
                                        aria-describedby="button-addon2"
                                        onChange={(e) => {
                                            searchGateway(e.target.value);
                                        }}
                                    />
                                </InputGroup>
                            </div>
                        </Col>
                    </Row>
                    <Row className="cstm-table mt-4">
                        <Col sm={12}>
                            <div className="table-head">
                                <Row className="no-checkbox">
                                    <Col>Gateway Name {sortIcon("name")}</Col>
                                    <Col>UUID {sortIcon("gateway_id")}</Col>
                                    <Col>
                                        Last Active {sortIcon("heartbeat")}
                                    </Col>

                                    <Col sm="1">Action</Col>
                                    <Col sm="1"></Col>
                                </Row>
                            </div>
                            {renderStandalone()}
                            <div
                                className={
                                    standaloneGatewayList.length === 0
                                        ? "mt-5 text-center d-block"
                                        : "mt-5 text-center d-none"
                                }
                            >
                                No gateway found.
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            <Modal
                centered
                show={modalShow}
                onHide={closeConfirmModal}
                backdrop="static"
                keyboard={false}
                aria-labelledby="example-modal-sizes-title-sm"
                className="no-header danger"
            >
                <Modal.Body className="text-center mt-3 mb-3">
                    <div className="modal-icon-box">
                        <span className="material-icons">delete</span>
                    </div>
                    <h3 className="mb-3">Delete Gateway?</h3>
                    <p className="mb-4">{modalContent}</p>
                    <>
                        <Button variant="secondary" onClick={closeConfirmModal}>
                            CANCEL
                        </Button>
                        <Button variant="danger" onClick={handleDeleteGateway}>
                            DELETE
                        </Button>
                    </>
                </Modal.Body>
            </Modal>
            <RemindDownloadDataModal
                show={showRemindDownload}
                onConfirm={handleRemindDownload}
            />
        </ContentWrapper>
    );
};

export { AllGateway, GatewayGroups, Standalone };
